import React from "react";

export const NAV = [
    { to: "/", label: "Home", no: "00" },
    { to: "/what-is-facial-recognition", label: "What is Facial Recognition?", no: "01" },
    { to: "/frt-and-cctv", label: "Facial Recognition + CCTV", no: "02" },
    { to: "/new-zealand-context", label: "New Zealand Context", no: "03" },
    { to: "/benefits", label: "Benefits & Opportunities", no: "04" },
    { to: "/risks", label: "Risks & Challenges", no: "05" },
    { to: "/ethics", label: "Ethical & Social Issues", no: "06" },
    { to: "/governance-options", label: "Governance Options", no: "07" },
    { to: "/governance-options/human-verification", label: "Option 1 — Human Verification", no: "07.1", child: true },
    { to: "/governance-options/independent-oversight", label: "Option 2 — Independent Oversight", no: "07.2", child: true },
    { to: "/governance-options/temporary-restriction", label: "Option 3 — Temporary Restriction", no: "07.3", child: true },
    { to: "/our-research", label: "Our Research / Findings", no: "08" },
    { to: "/group-process", label: "Group Process", no: "09" },
    { to: "/references", label: "References", no: "10" },
];

export const MEMBERS = [
    {
        name: "Nico",
        role: "Team Leader",
        initials: "N",
        responsibilities:
            "Organises meetings, keeps the group on schedule, coordinates the proposal presentation and maintains momentum across all project stages.",
        bio: "Leads the group's overall direction and quality control, with a particular interest in the legislative lag between biometric technology and New Zealand law.",
    },
    {
        name: "Stacy",
        role: "Editor",
        initials: "S",
        responsibilities:
            "Manages the website's structure and content, owns the page architecture, and is responsible for final quality checks before publication.",
        bio: "Turns research into a coherent, readable website — responsible for editing, consistency of voice, and the scholarly presentation of every page.",
    },
    {
        name: "Moelili'a",
        role: "Researcher",
        initials: "M",
        responsibilities:
            "Finds and evaluates reliable sources, and leads research on bias, privacy, and regulation in New Zealand and comparable jurisdictions.",
        bio: "Built the evidence base for this dossier — from NIST accuracy studies to Privacy Commissioner inquiries — and keeps every claim traceable to a source.",
    },
];

export const STATS = [
    { value: "~180", label: "Clearview AI searches run by NZ Police in the unapproved 2020 trial", cite: "IPCA, 2020" },
    { value: "225.97M", label: "faces scanned during the Foodstuffs live FRT trial (Feb–Sep 2024)", cite: "OPC, 2025" },
    { value: "0", label: "dedicated biometric statutes in New Zealand — only the Privacy Act 2020's general principles", cite: "OPC, 2024" },
    { value: "3", label: "governance options our research examines for how NZ could move forward", cite: "This project" },
];

export const GOV_OPTIONS = [
    {
        slug: "human-verification",
        no: "07.1",
        title: "Human Verification",
        tagline: "No action is ever taken on an algorithm's word alone.",
        icon: "scan-face",
        short:
            "Police must confirm every automated match with trained human judgement before any action is taken — a 'human in the loop' safeguard.",
        where: "Precedent: Foodstuffs' two-staff verification rule (OPC, 2025)",
        strength: "Directly interrupts false positives before they become wrongful arrests.",
        weakness: "Humans are prone to 'automation bias' — deferring to the machine — and verification does nothing about who gets scanned in the first place.",
    },
    {
        slug: "independent-oversight",
        no: "07.2",
        title: "Independent Oversight",
        tagline: "Clear rules on biometric data, set and checked from outside.",
        icon: "scale",
        short:
            "A statutory framework: independent approval of deployments, mandatory privacy impact assessments, accuracy thresholds, audit powers and public transparency.",
        where: "Precedent: Bridges v South Wales Police; OPC Biometric Processing Privacy Code",
        strength: "Fixes the structural problem — the 2020 Clearview trial happened because oversight was internal and optional.",
        weakness: "Slower to establish, depends on sustained funding and political will, and can lag behind rapid technical change.",
    },
    {
        slug: "temporary-restriction",
        no: "07.3",
        title: "Temporary Restriction",
        tagline: "A moratorium until bias and false positives are fixed.",
        icon: "ban",
        short:
            "A precautionary pause on police FRT use in public spaces until error rates are demographically fair, safeguards are proven, and law catches up.",
        where: "Precedent: San Francisco's 2019 ban; European Parliament's call for a moratorium",
        strength: "The strongest protection against wrongful accusation while safeguards are immature.",
        weakness: "Foregoes real benefits (missing-person searches, safeguarding), and 'temporary' bans can quietly become permanent — or quietly lapse.",
    },
];

export const MINUTES = [
    {
        no: "01",
        date: "Monday 20 July 2026",
        time: "4:00 – 4:45 pm",
        place: "Library study room (on campus)",
        present: ["Nico", "Stacy", "Moelili'a"],
        apologies: [],
        items: [
            "Confirmed group composition and allocated roles: Nico (Team Leader), Stacy (Editor), Moelili'a (Researcher).",
            "Brainstormed potential topics from the COMP501 topic list; settled on Facial Recognition Technology use by NZ Police.",
            "Agreed the topic is timely: the Privacy Commissioner's biometrics consultation is recent and police FRT policy changed in 2024.",
        ],
        decisions: ["Topic confirmed: FRT utilisation by the NZ Police, centred on false positives and the chilling effect."],
        actions: [
            { task: "Book weekly meeting slot; set up shared document folder", owner: "Nico" },
            { task: "Create first-draft site skeleton with page list", owner: "Stacy" },
            { task: "Gather initial academic sources on FRT accuracy", owner: "Moelili'a" },
        ],
    },
    {
        no: "02",
        date: "Monday 3 August 2026",
        time: "4:00 – 5:00 pm",
        place: "Library study room (on campus)",
        present: ["Nico", "Stacy", "Moelili'a"],
        apologies: [],
        items: [
            "Reviewed Moelili'a's source list: NIST demographic-effects study, Gender Shades, IPCA investigation, Privacy Commissioner inquiry reports.",
            "Agreed three governance options will structure the recommendations: human verification, independent oversight, temporary restriction.",
            "Stacy presented draft page structure; group approved the eleven-page architecture.",
        ],
        decisions: ["Final site architecture approved; governance options get a hub page plus one page per option."],
        actions: [
            { task: "Draft Risks & Challenges page focusing on false positives", owner: "Nico" },
            { task: "Deep-dive: NZ Privacy Act 2020 gaps for biometrics", owner: "Moelili'a" },
        ],
    },
    {
        no: "03",
        date: "Monday 24 August 2026",
        time: "4:00 – 5:15 pm",
        place: "Online (video call)",
        present: ["Nico", "Stacy", "Moelili'a"],
        apologies: [],
        items: [
            "Content walkthrough: NZ Context timeline checked against IPCA and RNZ reporting for accuracy of dates and numbers.",
            "Discussed the Foodstuffs inquiry figures (225.97 million faces scanned; 90% → 92.5% threshold change) and agreed to use them as the key NZ case study.",
            "Agreed imagery approach: treated black-and-white photography with figure captions, APA in-text citations throughout.",
        ],
        decisions: ["All statistics must carry an in-text citation and appear on the References page in APA 7 format."],
        actions: [
            { task: "Compile References page; cross-check every citation", owner: "Moelili'a" },
            { task: "Apply consistent figure-caption format across pages", owner: "Stacy" },
        ],
    },
    {
        no: "04",
        date: "Monday 14 September 2026",
        time: "4:00 – 5:00 pm",
        place: "Library study room (on campus)",
        present: ["Nico", "Moelili'a"],
        apologies: ["Stacy (family commitment — sent page notes in advance)"],
        items: [
            "Reviewed Stacy's written notes on Ethical & Social Issues page; approved inclusion of Māori data sovereignty discussion.",
            "Nico and Moelili'a refined the 'chilling effect' argument for the Risks page.",
            "Reviewed timeline against due dates: proposal slide due 3 October; full site due 17 October, 4:00 pm.",
        ],
        decisions: ["Buffer week reserved (5–10 Oct) for proofreading and final link checks."],
        actions: [
            { task: "Editorial pass on Benefits & Opportunities and Ethics pages", owner: "Stacy" },
            { task: "Finalise governance options pages", owner: "Moelili'a" },
            { task: "Track milestones to submission dates", owner: "Nico" },
        ],
    },
    {
        no: "05",
        date: "Monday 5 October 2026",
        time: "4:00 – 5:30 pm",
        place: "Library study room (on campus)",
        present: ["Nico", "Stacy", "Moelili'a"],
        apologies: [],
        items: [
            "Full site read-through; minor edits agreed on home page summary and findings list.",
            "Checked that 1–3+ figures, team member page, meeting minutes and APA references are all present per the marking criteria.",
            "Confirmed who submits: one member to upload the zip file and the individual documents by 17 October, 4:00 pm.",
        ],
        decisions: ["Site is content-complete; proceed to final proofread and submission."],
        actions: [
            { task: "Final proofread and broken-link check", owner: "Stacy" },
            { task: "Submit website zip on behalf of the group", owner: "Nico" },
        ],
    },
];

export const REFERENCES = [
    {
        id: "ajana-2013",
        group: "Academic sources",
        apa: 'Ajana, B. (2013). <i>Governing through biometrics: The biopolitics of identity</i>. Palgrave Macmillan.',
    },
    {
        id: "buolamwini-2018",
        group: "Academic sources",
        apa: "Buolamwini, J., & Gebru, T. (2018). Gender shades: Intersectional accuracy disparities in commercial gender classification. <i>Proceedings of Machine Learning Research, 81</i>, 1–15.",
    },
    {
        id: "grother-2019",
        group: "Academic sources",
        apa: "Grother, P., Ngan, M., & Hanaoka, K. (2019). <i>Face recognition vendor test (FRVT) Part 3: Demographic effects</i> (NISTIR 8280). National Institute of Standards and Technology. https://doi.org/10.6028/NIST.IR.8280",
    },
    {
        id: "introna-2010",
        group: "Academic sources",
        apa: "Introna, L., & Nissenbaum, H. (2010). <i>Facial recognition technology: A survey of policy and implementation issues</i> (Working Paper No. 10/05). New York University, Center for Catastrophe Preparedness & Response.",
    },
    {
        id: "lyon-2018",
        group: "Academic sources",
        apa: "Lyon, D. (2018). <i>The culture of surveillance: Watching as a way of life</i>. Polity Press.",
    },
    {
        id: "mann-2017",
        group: "Academic sources",
        apa: "Mann, M., & Smith, M. (2017). Automated facial recognition technology: Recent developments and approaches to oversight. <i>University of New South Wales Law Journal, 40</i>(1), 121–145.",
    },
    {
        id: "hill-2020",
        group: "Journalism & media",
        apa: "Hill, K. (2020, June 24). Wrongfully arrested because face recognition can't tell Black people apart. <i>The New York Times</i>.",
    },
    {
        id: "hill-2023",
        group: "Journalism & media",
        apa: "Hill, K. (2023, May 22). Another arrest, and jail time, due to a bad facial recognition match. <i>The New York Times</i>.",
    },
    {
        id: "rnz-2020-trial",
        group: "Journalism & media",
        apa: "Radio New Zealand. (2020, June 24). <i>Police trialled facial recognition tech without clearance</i>. https://www.rnz.co.nz/news/science-and-technology/416483/police-trialled-facial-recognition-tech-without-clearance",
    },
    {
        id: "rnz-2020-stocktake",
        group: "Journalism & media",
        apa: "Radio New Zealand. (2020, November 30). <i>Police stocktake surveillance tech after Clearview AI facial recognition trial</i>. https://www.rnz.co.nz/news/science-and-technology/416913",
    },
    {
        id: "rnz-2024-policy",
        group: "Journalism & media",
        apa: "Radio New Zealand. (2024). <i>NZ Police finally has facial recognition policy — but is it strict enough?</i> https://www.rnz.co.nz/news/business_media/525642",
    },
    {
        id: "ipca-2020",
        group: "Official reports & inquiries",
        apa: "Independent Police Conduct Authority. (2020, November 30). <i>Use of emergent technologies: Summary of investigation</i>. https://www.ipca.govt.nz/Site/Outcomes/2019-20-summaries-of-police-investigations/2020-nov-30-use-of-emergent-technologies.aspx",
    },
    {
        id: "opc-2024",
        group: "Official reports & inquiries",
        apa: "Office of the Privacy Commissioner. (2024, December 18). <i>Statutory consultation paper for a Biometric Processing Privacy Code</i>. https://www.privacy.org.nz/",
    },
    {
        id: "opc-2025",
        group: "Official reports & inquiries",
        apa: "Office of the Privacy Commissioner. (2025, June 3). <i>Inquiry into Foodstuffs North Island's trial use of facial recognition technology: Report of the Privacy Commissioner</i>. https://www.privacy.org.nz/",
    },
    {
        id: "tmr-2018",
        group: "Official reports & inquiries",
        apa: "Te Mana Raraunga. (2018). <i>Te Mana Raraunga: Māori Data Sovereignty Network charter</i>. https://www.temanararunga.maori.nz/",
    },
    {
        id: "bridges-2020",
        group: "Legislation & case law",
        apa: "Bridges v South Wales Police [2020] EWCA Civ 1058 (Court of Appeal of England and Wales).",
    },
    {
        id: "nzbra-1990",
        group: "Legislation & case law",
        apa: "New Zealand Bill of Rights Act 1990.",
    },
    {
        id: "privacy-act-2020",
        group: "Legislation & case law",
        apa: "Privacy Act 2020 (New Zealand).",
    },
];

export const NEXT_MAP = {
    "/": { to: "/what-is-facial-recognition", no: "01", label: "What is Facial Recognition?" },
    "/what-is-facial-recognition": { to: "/frt-and-cctv", no: "02", label: "Facial Recognition + CCTV" },
    "/frt-and-cctv": { to: "/new-zealand-context", no: "03", label: "New Zealand Context" },
    "/new-zealand-context": { to: "/benefits", no: "04", label: "Benefits & Opportunities" },
    "/benefits": { to: "/risks", no: "05", label: "Risks & Challenges" },
    "/risks": { to: "/ethics", no: "06", label: "Ethical & Social Issues" },
    "/ethics": { to: "/governance-options", no: "07", label: "Governance Options" },
    "/governance-options": { to: "/governance-options/human-verification", no: "07.1", label: "Option 1 — Human Verification" },
    "/governance-options/human-verification": { to: "/governance-options/independent-oversight", no: "07.2", label: "Option 2 — Independent Oversight" },
    "/governance-options/independent-oversight": { to: "/governance-options/temporary-restriction", no: "07.3", label: "Option 3 — Temporary Restriction" },
    "/governance-options/temporary-restriction": { to: "/our-research", no: "08", label: "Our Research / Findings" },
    "/our-research": { to: "/group-process", no: "09", label: "Group Process" },
    "/group-process": { to: "/references", no: "10", label: "References" },
    "/references": { to: "/", no: "00", label: "Back to Home" },
};
